import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, LayoutZeile } from "@kundeninfo/api-client";
import { Objektliste, Objektzeile, useBeforeUnload, useBestaetigung } from "@kundeninfo/ui";
import type { Objektstatus } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import type { VerlassenWaechter } from "../../router.js";
import { LayoutEditor } from "../../components/layout/LayoutEditor.js";
import type { LayoutEditorClient } from "../../components/layout/LayoutEditor.js";
import { Meldung, meldungAus } from "./gemeinsam.js";
import type { VerwaltungBereichTeile } from "./gemeinsam.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich LAYOUT (Spec „Layout-Editor, Etappe 1", R1): die Unternehmen mit
 * ihrem einen Layout „Standard" als Liste, darüber der Editor als vollflächiger
 * Arbeitsdialog (Paket „Editor-Arbeitsdialog“). Die Reihenfolge der
 * Liste kommt vom Server (Schlechtestes oben: Störung, Entwurf geändert, nicht
 * freigegeben, freigegeben).
 *
 * Ein HOOK wie die übrigen Objektbereiche (siehe `VerwaltungBereichTeile`). Solange der
 * geöffnete Entwurf ungespeichert ist, fragt jede Navigation nach, und die Liste ist
 * gesperrt.
 */

export type LayoutClient = LayoutEditorClient & Pick<ApiClient, "listLayouts">;

export type VerwaltungLayoutProps = {
  aktiv: boolean;
  client: LayoutClient;
  layoutId: string | null;
  /** Das aktive Unternehmen des Fensters: Die Liste zeigt nur seine Layouts (E364), und ohne
   *  Layout in der Adresse öffnet der Bereich sein Vorgabe-Layout zuerst. */
  arbeitsunternehmenId: string | null;
  onOeffnen: (layoutId: string | null) => void;
  onUnternehmen: (companyId: string) => void;
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

function status(z: LayoutZeile): Objektstatus {
  // Layout-Varianten (Migration 049): eine archivierte Variante wirkt nur noch für Belege, die sie gewählt haben.
  if (z.archiviertAm !== null) return { text: "archiviert", art: "aus" };
  if (z.stoerung === "ABLAGE_NICHT_ERREICHBAR") {
    // Der Hinweis des Servers unterscheidet „antwortet mit Fehler“ von „nicht erreichbar“
    // (Nachlese LE1); ohne Hinweis der feste Wortlaut.
    return { text: z.ablageHinweis ?? "Ablage nicht erreichbar", art: "warnung" };
  }
  if (z.stoerung !== null) return { text: "Störung", art: "fehler" };
  if (z.geaendert) return { text: "Entwurf geändert", art: "warnung" };
  if (z.freigegebenAm === null) return { text: "nicht freigegeben", art: "aus" };
  return { text: "freigegeben", art: "ok" };
}

export function useVerwaltungLayout(p: VerwaltungLayoutProps): VerwaltungBereichTeile {
  const [zeilen, setZeilen] = useState<LayoutZeile[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [neuLaden, setNeuLaden] = useState(0);
  const [ungespeichert, setUngespeichert] = useState(false);
  // Synchron mitgeführt: Der Verlassen-Wächter liest ihn im selben Takt, in dem
  // `onSchliessen` nach dem Verwerfen zur Liste navigiert (der Effekt, der ihn abmeldet,
  // käme erst danach).
  const ungespeichertRef = useRef(false);
  ungespeichertRef.current = ungespeichert;
  /*
   * EDITOR ZU (Paket „Editor-Arbeitsdialog“, Entscheid 55): Der Editor ist ein Arbeitsdialog
   * über der Liste; Schliessen führt zur Liste zurück. Damit das Vorgabe-Layout des
   * Arbeitsunternehmens danach nicht sofort wieder aufgeht, merkt sich der Bereich das
   * Schliessen — bis zum nächsten Listenklick oder zum nächsten Betreten des Bereichs.
   */
  const [editorZu, setEditorZu] = useState(false);
  /*
   * QUITTUNG DES LÖSCHENS (Layout-Varianten, LV7): Die gelöschte Zeile kommt nicht wieder in die Liste — ein liegen
   * gebliebener Eintrag der Engine (`ablageHinweis`) hätte sonst keinen Ort. Eine Meldung über der Liste, bis das
   * nächste Layout aufgeht.
   */
  const [quittung, setQuittung] = useState<{ text: string; art: "ok" | "warnung" } | null>(null);

  /*
   * Das GEÖFFNETE Layout: das aus der Adresse, sonst das des Arbeitsunternehmens (ME-Etappe
   * 1, Aufgabe 7). Bewusst ohne Navigation — die Adresse bleibt `/verwaltung/layout`: Ein
   * Ersetzen der Adresse machte die Zurück-Taste zur Schleife, und auf schmalem Gerät
   * bliebe die Liste zuerst sichtbar (`objektOffen` hängt an der Adresse).
   *
   * Solange der Entwurf ungespeichert ist, hält der Bereich die Vorgabe fest: Eine neu
   * geladene Liste mit anderer Vorgabe risse sonst den Editor samt Entwurf aus dem Baum. Der
   * Wechsel des aktiven Unternehmens fragt seit der Mandantentrennung selbst über den
   * Verlassen-Wächter und lädt das Fenster neu (R4).
   */
  // Belegdesign folgt dem aktiven Unternehmen (E364); gefiltert wird hier, die Verwaltungswege des Servers bleiben (R14).
  const sichtbar = (zeilen ?? []).filter((z) => z.companyId === p.arbeitsunternehmenId);
  const vorgabeRef = useRef<string | null>(null);
  if (!ungespeichert) {
    vorgabeRef.current = sichtbar.find((z) => z.vorgabe)?.layoutId ?? null;
  }
  const { aktiv } = p;
  const layoutId = p.layoutId ?? (editorZu ? null : vorgabeRef.current);
  // Ein Lesezeichen auf das Layout eines ANDEREN Unternehmens öffnet keinen Editor (E364, wie Textbausteine
  // und Vorlagen). Nur aus der Adresse — die festgehaltene Vorgabe eines ungespeicherten Entwurfs bleibt —
  // und nur ein bekanntes fremdes: Eine eben angelegte Variante steht noch nicht in der Liste.
  const fremd = p.layoutId !== null
    && (zeilen ?? []).some((z) => z.layoutId === p.layoutId && z.companyId !== p.arbeitsunternehmenId);
  useEffect(() => { if (layoutId !== null) setQuittung(null); }, [layoutId]);

  const listLayouts = p.client.listLayouts;
  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    listLayouts()
      .then((a) => { if (!abgebrochen) { setZeilen(a.items); setLadeFehler(null); } })
      .catch((f: unknown) => {
        if (!abgebrochen) setLadeFehler(meldungAus(f, "Die Layouts konnten nicht geladen werden."));
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listLayouts, neuLaden]);

  useEffect(() => {
    if (aktiv) return;
    setUngespeichert(false);
    setLadeFehler(null);
    setEditorZu(false);
  }, [aktiv]);

  // Ein anderes Arbeitsunternehmen ist ein neuer Zusammenhang: Sein Vorgabe-Layout öffnet
  // wieder, auch wenn das vorige geschlossen wurde (Review A2 H1 — in der Belegdesign-App
  // der natürliche Weg zum nächsten Layout).
  const arbeitsunternehmenId = p.arbeitsunternehmenId;
  useEffect(() => { setEditorZu(false); }, [arbeitsunternehmenId]);

  useBeforeUnload(ungespeichert);

  const { bestaetigen } = useBestaetigung();
  const registriere = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!ungespeichert || registriere === undefined) return;
    return registriere(async () => {
      // Schon verworfen (Schliessen des Editors), der Effekt hat nur noch nicht abgemeldet.
      if (!ungespeichertRef.current) return true;
      const weiter = await bestaetigen({
        titel: "Seite verlassen?",
        text: "Der Layout-Entwurf hat ungespeicherte Änderungen. Sie gehen verloren.",
        bestaetigenText: "Verlassen",
        gefaehrlich: true,
      });
      if (weiter) setUngespeichert(false);
      return weiter;
    });
  }, [ungespeichert, registriere]);

  /** Der Editor ist zu (nach der Verwerfen-Rückfrage): zurück zur Liste. Die Adresse
   *  bleibt bzw. wird `/verwaltung/layout` — ohne Layout in der Adresse. */
  const onOeffnen = p.onOeffnen;
  const onSchliessen = useCallback(() => {
    ungespeichertRef.current = false;
    setUngespeichert(false);
    setEditorZu(true);
    if (p.layoutId !== null) onOeffnen(null);
  }, [p.layoutId, onOeffnen]);

  const onZustand = useCallback((z: { ungespeichert: boolean; listeNeu: boolean }) => {
    setUngespeichert(z.ungespeichert);
    if (z.listeNeu) setNeuLaden((n) => n + 1);
  }, []);

  // Ein Layout aus der Adresse ohne geladene Liste: Der Ladefehler steht im Detail (dort, wo der Editor fehlt) —
  // EINMAL, nicht zugleich in der Liste (Review Aufgabe 16 K5).
  const ausAdresse = p.layoutId !== null;
  const fehlerImDetail = ausAdresse && zeilen === null && ladeFehler !== null;
  const liste = (
    <>
      {ladeFehler !== null && !fehlerImDetail && <Meldung text={ladeFehler} />}
      {/* `me-hinweis` setzt für ok/warnung selbst role="status". */}
      {quittung !== null && <Hinweis art={quittung.art}>{quittung.text}</Hinweis>}
      {ladeFehler === null && zeilen === null && (
        <p className="vw__hinweis" role="status">Lädt …</p>
      )}
      <Objektliste label="Layouts">
        {sichtbar.map((z) => {
          // Die Marke „Vorgabe" nur, wo das Unternehmen mehrere Layouts führt — sonst ist sie Rauschen.
          const mehrere = sichtbar.length > 1;
          const marken = [...(z.vorgabe && mehrere ? ["Vorgabe"] : []), ...(z.aktiv ? [] : ["stillgelegt"])];
          return (
          <Objektzeile key={z.layoutId} name={z.unternehmen} kennung={z.kuerzel}
            meta={`Layout „${z.name}“`} status={status(z)}
            {...(marken.length === 0 ? {} : { marken })}
            gewaehlt={z.layoutId === layoutId} gesperrt={ungespeichert}
            // Die Vorgabe behält den Namen aus Etappe 1 (Bestandstests und E2E-Helfer suchen ihn).
            ariaLabel={z.vorgabe ? `Layout von ${z.unternehmen} öffnen`
              : `Layout „${z.name}“ von ${z.unternehmen} öffnen`}
            onOeffnen={() => { setEditorZu(false); p.onOeffnen(z.layoutId); }} />
          );
        })}
      </Objektliste>
    </>
  );

  // Ein Layout aus der Adresse öffnet erst mit der Liste — sonst ginge ein fremdes kurz auf. Scheitert die
  // Liste, bleibt es beim Fehler: Ohne sie lässt sich nicht sagen, wem das Layout gehört (kein Editor „auf Verdacht").
  // Ohne aktives Unternehmen gehört keines zum aktiven — der Hinweis sagt das, statt auf „ein anderes" zu verweisen.
  const detail: ReactElement = ausAdresse && p.arbeitsunternehmenId === null
    ? (
      <p className="vw__hinweis">
        Es ist kein Unternehmen aktiv. Ohne aktives Unternehmen zeigt dieser Bereich keine Layouts.
      </p>
    )
    : ausAdresse && zeilen === null && ladeFehler === null
    ? <p className="vw__hinweis" role="status">Lädt …</p>
    : fehlerImDetail
    ? <Meldung text={ladeFehler} />
    : fremd
    ? (
      <p className="vw__hinweis">
        Dieses Layout gehört zu einem anderen als dem aktiven Unternehmen. Bitte links eines wählen.
      </p>
    )
    : layoutId === null
    ? (
      <p className="vw__hinweis">
        Links ein Layout des aktiven Unternehmens wählen — es steht dann hier. Akzentfarbe und
        Logo werden am Unternehmen gepflegt.
      </p>
    )
    : <LayoutEditor key={layoutId} client={p.client} layoutId={layoutId}
        onUnternehmen={p.onUnternehmen} onZustand={onZustand} onSchliessen={onSchliessen}
        onLayoutOeffnen={(id) => { setEditorZu(false); onOeffnen(id); }}
        onGeloescht={(name, ablageHinweis) => setQuittung(ablageHinweis === null
          ? { text: `„${name}“ ist gelöscht.`, art: "ok" }
          : { text: `„${name}“ ist gelöscht. ${ablageHinweis}`, art: "warnung" })} />;

  return { liste, detail: detail };
}
